/**
 * Succession is a panel, not a sentence.
 *
 * Each edge is a link to a real register, with its status, its Incomplete badge and the matter it
 * covers where the edge is matter-scoped — because a register's trade marks and its patents need
 * not have passed to the same successor.
 *
 * Adding a successor by name creates the register if the engine does not hold it. The UI says so
 * before it happens: creating a record as a side effect of typing a name is otherwise a surprise.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '~/ui/Button';
import { Pill } from '~/ui/Pill';
import { Field, TextInput, DateInput, Select } from '~/ui/Field';
import { EmptyState } from '~/ui/Feedback';
import { ConfirmDialog } from '~/ui/Modal';
import { useAddSuccession, useRemoveSuccession } from '~/api/mutations';
import { useToast } from '~/hooks/useToast';
import { problemsOf } from '~/api/client';
import { fmtDate } from '~/lib/format';
import { routes } from '~/lib/routes';
import type { SuccessorRef, VocabularyTerm } from '~/api/schema';
import styles from './Overview.module.css';

export interface SuccessionPanelProps {
  code: string;
  successors: readonly SuccessorRef[];
  predecessors: readonly SuccessorRef[];
  matters: readonly VocabularyTerm[];
  knownCodes: readonly string[];
  today: string;
}

export function SuccessionPanel({
  code,
  successors,
  predecessors,
  matters,
  knownCodes,
  today,
}: SuccessionPanelProps) {
  const [adding, setAdding] = useState(false);

  return (
    <>
      <h3 style={{ fontSize: 12.5, marginBottom: 8 }}>Rights passed to</h3>
      <EdgeList code={code} edges={successors} matters={matters} removable emptyText="No register is recorded as having taken this register's rights." />

      <h3 style={{ fontSize: 12.5, margin: '18px 0 8px' }}>Rights came from</h3>
      <EdgeList code={code} edges={predecessors} matters={matters} emptyText="No register is recorded as having passed its rights here." />

      <div className={styles.addRow}>
        <Button onClick={() => setAdding(true)}>Add a successor</Button>
      </div>

      {adding ? (
        <AddSuccessionDialog
          code={code}
          matters={matters}
          knownCodes={knownCodes}
          today={today}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </>
  );
}

function EdgeList({
  code,
  edges,
  matters,
  removable = false,
  emptyText,
}: {
  code: string;
  edges: readonly SuccessorRef[];
  matters: readonly VocabularyTerm[];
  removable?: boolean;
  emptyText: string;
}) {
  const remove = useRemoveSuccession(code);
  const toast = useToast();

  if (edges.length === 0) {
    return (
      <EmptyState variant="bare">
        <p>{emptyText}</p>
      </EmptyState>
    );
  }

  const label = (key: string) => matters.find((matter) => matter.code === key)?.label ?? key;

  return (
    <ul className={styles.succession}>
      {edges.map((edge) => (
        <li key={edge.id} className={styles.successionRow}>
          <Link to={routes.overview(edge.code)} className={styles.successionName}>
            {edge.name}
          </Link>
          <Pill tone={edge.status === 'active' ? 'done' : 'neutral'} size="mini">
            {edge.status === 'active' ? 'Active' : 'Dead'}
          </Pill>
          {edge.incomplete ? (
            <Pill tone="quest" size="mini">
              Incomplete
            </Pill>
          ) : null}
          <span className={styles.successionMeta}>
            {edge.matter ? `${label(edge.matter)} only` : 'All matters'} · from{' '}
            {fmtDate(edge.effectiveFrom)}
            {edge.note ? ` · ${edge.note}` : ''}
          </span>
          <span className={styles.spacer} />
          {removable ? (
            <Button
              size="sm"
              variant="ghost"
              pending={remove.isPending && remove.variables === edge.id}
              onClick={() =>
                remove.mutate(edge.id, {
                  onSuccess: () => toast.show('ok', `The edge to ${edge.name} was removed.`),
                  onError: (error) => {
                    for (const problem of problemsOf(error)) toast.show('error', problem);
                  },
                })
              }
            >
              Remove
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function AddSuccessionDialog({
  code,
  matters,
  knownCodes,
  today,
  onClose,
}: {
  code: string;
  matters: readonly VocabularyTerm[];
  knownCodes: readonly string[];
  today: string;
  onClose: () => void;
}) {
  const add = useAddSuccession(code);
  const toast = useToast();

  const [name, setName] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [matter, setMatter] = useState('');
  const [note, setNote] = useState('');

  const trimmed = name.trim();
  const willCreate = trimmed.length > 0 && !knownCodes.includes(trimmed.toUpperCase());

  return (
    <ConfirmDialog
      title="Add a successor"
      subtitle="A successor is always a real register, never a name in a text column."
      confirmLabel="Record the succession"
      cancelLabel="Cancel"
      pending={add.isPending}
      onCancel={onClose}
      onConfirm={() =>
        add.mutate(
          {
            successorName: trimmed,
            effectiveFrom,
            matter: matter || null,
            note: note || null,
          },
          {
            onSuccess: () => {
              toast.show('ok', `Rights recorded as passing to ${trimmed}.`);
              onClose();
            },
            onError: (error) => {
              for (const problem of problemsOf(error)) toast.show('error', problem);
            },
          },
        )
      }
      body={
        <>
          <Field
            label="Successor"
            hint={
              willCreate
                ? `${trimmed} is not a register we hold. Adding it will create it, marked incomplete, and file it as research to do.`
                : 'The register rights passed to, by name or by code.'
            }
          >
            <TextInput value={name} onChange={(event) => setName(event.currentTarget.value)} />
          </Field>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            <Field label="Rights passed on" hint="Normally the predecessor's closure.">
              <DateInput value={effectiveFrom} onChange={setEffectiveFrom} />
            </Field>
            <Field
              label="Matter"
              hint="A register's trade marks and its patents need not have passed to the same successor."
            >
              <Select
                value={matter}
                onChange={setMatter}
                options={[
                  { value: '', label: 'All matters' },
                  ...matters.map((term) => ({ value: term.code, label: term.label })),
                ]}
              />
            </Field>
          </div>
          <Field label="Note" hint="Anything worth recording about the passage.">
            <TextInput value={note} onChange={(event) => setNote(event.currentTarget.value)} />
          </Field>
        </>
      }
    />
  );
}
